export const SUGGESTION_ACCEPTED = 'accepted';
export const SUGGESTION_SKIPPED = 'skipped';

export const DETECTION_QUEUED = 'queued';
export const DETECTION_PROCESSING = 'processing';
export const DETECTION_FAILED = 'failed';

// vod attaches this to the video in the dashboard's own responses only: the
// suggestions are unreviewed model output, which the public API never carries.
//
// The list and the single-video response carry the same shape on purpose —
// useVideoQuery seeds the sidebar from the list cache, so a summarised list
// payload would reach the sidebar looking like a complete one.
const getAiProductSuggestions = (video) => video?.aiProductSuggestions;

export const getPendingSuggestions = (video) => getAiProductSuggestions(video)?.suggestions ?? [];

export const isDetectingProducts = (detection) =>
  detection?.status === DETECTION_QUEUED || detection?.status === DETECTION_PROCESSING;

// Pending suggestions alone cannot distinguish an empty run from a reviewed one.
export const hasNoProductsFound = (detection) =>
  detection?.status === 'succeeded' && detection.suggestionCount === 0;

// Always open on suggestions to review. Without added products, also show a
// running check or a completed empty result without unfolding the section.
export const shouldOpenProductsByDefault = (products, suggestions, detection) =>
  suggestions.length > 0 || (products.length === 0 && (isDetectingProducts(detection) || hasNoProductsFound(detection)));

const MINUTE_MS = 60 * 1000;

// vod never gives up on a run, so the page does: a run still under way this
// long after its estimate, or its start without one, shows as failed. Should it
// finish after all, the videos stream brings its results in as usual.
const GIVE_UP_AFTER_MS = 60 * MINUTE_MS;

/**
 * The latest AI product tagging run on the video, as `{ status, startedAt,
 * expectedFinishAt, finishedAt }`, or null if it has never been checked. A run
 * the page has given up on (see GIVE_UP_AFTER_MS) comes back as failed.
 */
export const getProductDetection = (video, now = Date.now()) => {
  const detection = getAiProductSuggestions(video)?.detection ?? null;
  if (!isDetectingProducts(detection)) return detection;
  const since = Date.parse(detection.expectedFinishAt ?? detection.startedAt);
  if (Number.isFinite(since) && now - since > GIVE_UP_AFTER_MS) return { ...detection, status: DETECTION_FAILED };
  return detection;
};

// The bar is this full at the estimated finish, so a run that takes longer
// than expected still has somewhere to go.
const PROGRESS_AT_ESTIMATE = 0.9;
// ...and never fuller than this while the run is under way, so it cannot be
// mistaken for a finished one.
const MAX_PROGRESS = 0.95;
// Past the estimate, the bar covers half of what is left to MAX_PROGRESS this
// often: it keeps moving for as long as the run lasts.
const OVERTIME_HALF_LIFE_MS = 2 * MINUTE_MS;

const approachMax = (progress, since, now) =>
  MAX_PROGRESS - (MAX_PROGRESS - progress) * 0.5 ** (Math.max(0, now - since) / OVERTIME_HALF_LIFE_MS);

/**
 * How far along to show a run, as `{ progress, minutesLeft }` with `progress`
 * from 0 to 1, or null when there is no estimate to go by.
 *
 * The bar fills from `from` (`{ progress, at }`, by default empty when the run
 * started) toward the pipeline's estimate, and past it keeps creeping toward
 * MAX_PROGRESS. The time left is rounded up to whole minutes, and is 0 once the
 * estimate has passed while the run is still under way.
 */
export const getDetectionProgress = (detection, now = Date.now(), from = null) => {
  const expectedFinish = Date.parse(detection?.expectedFinishAt);
  if (!isDetectingProducts(detection) || !Number.isFinite(expectedFinish)) return null;
  const minutesLeft = Math.max(0, Math.ceil((expectedFinish - now) / MINUTE_MS));
  // A queued run has not started, so none of the time has gone yet.
  const started = Date.parse(detection.startedAt);
  const start = from ?? { progress: 0, at: Number.isFinite(started) ? Math.min(started, now) : now };
  if (start.at >= expectedFinish) {
    return { progress: approachMax(start.progress, start.at, now), minutesLeft };
  }

  const atEstimate = start.progress < PROGRESS_AT_ESTIMATE
    ? PROGRESS_AT_ESTIMATE
    : start.progress + (MAX_PROGRESS - start.progress) / 2;
  if (now >= expectedFinish) return { progress: approachMax(atEstimate, expectedFinish, now), minutesLeft };
  const share = Math.max(0, now - start.at) / (expectedFinish - start.at);
  return { progress: start.progress + (atEstimate - start.progress) * share, minutesLeft };
};

/**
 * Moves a run's bar on from what it showed last (`shown`, as this returned it,
 * or null), so it never goes back. When the estimate changes, the bar carries
 * on from where it is toward the new one instead of being measured against it
 * from the start. The time left follows a new estimate, up as well as down:
 * once segmentation has found the products, the pipeline knows better how long
 * the rest takes. Returns what to show now, as getDetectionProgress does, plus
 * what the next call needs, or null when there is nothing to show.
 */
export const advanceDetectionProgress = (shown, detection, now = Date.now()) => {
  const startedAt = detection?.startedAt ?? null;
  const sameRun = !!shown && shown.startedAt === startedAt;
  let from = null;
  if (sameRun) {
    from = shown.expectedFinishAt === detection.expectedFinishAt
      ? shown.from
      : { progress: shown.progress, at: shown.at };
  }
  const at = sameRun ? Math.max(now, shown.at) : now;
  const estimate = getDetectionProgress(detection, at, from);
  if (!estimate) return null;
  return {
    ...estimate,
    progress: sameRun ? Math.max(estimate.progress, shown.progress) : estimate.progress,
    at,
    from,
    startedAt,
    expectedFinishAt: detection.expectedFinishAt,
  };
};

/**
 * Shapes a suggestion like a product picked from the catalog, which is what the
 * rest of the video product flow expects: `ProductsListForProductEntity` reads
 * `title` / `brand` / `thumbnail`, and the save in `VideoDetailsContext` maps
 * `{ id, feed_id }` onto the request body.
 */
export const toStagedProduct = (suggestion) => ({
  id: suggestion.productId,
  feed_id: suggestion.feedId,
  title: suggestion.title,
  brand: suggestion.brand,
  thumbnail: suggestion.imageUrl,
});

// The catalog id and feed a product is identified by. A product loaded with
// the video keeps the catalog id in `sku` and its feed in camelCase `feedId`;
// its `id` is vod's own row id. One picked from the catalog, or staged from a
// suggestion, has the catalog id as `id` and its feed as `feed_id`, and may
// carry a `sku` field of its own that is not the catalog id. A product outside
// any feed is keyed on the org, as vod stores it. Same rule as
// `getUniqueProductId` in ProductCatalogPicker.
const catalogIdentity = (product, orgId) => (product?.feedId !== undefined
  ? { id: product.sku, feedId: product.feedId }
  : { id: product?.id, feedId: product?.feed_id || orgId });

export const matchesSuggestion = (product, suggestion, orgId) => {
  const identity = catalogIdentity(product, orgId);
  return identity.id === suggestion.productId && identity.feedId === (suggestion.feedId || orgId);
};

export const isProductStaged = (products = [], suggestion, orgId) =>
  products.some((product) => matchesSuggestion(product, suggestion, orgId));

/**
 * The reviews a save sends, as `{ id, status }`. An accepted review stands only
 * while its product is in the product list: removing the product after
 * pressing Add takes the review back, and the suggestion is pending again.
 */
export const getReviewsToSave = (reviews = {}, suggestions = [], products = [], orgId) =>
  suggestions.flatMap((suggestion) => {
    const status = reviews[suggestion.id];
    if (!status) return [];
    if (status === SUGGESTION_ACCEPTED && !isProductStaged(products, suggestion, orgId)) return [];
    return [{ id: suggestion.id, status }];
  });
